import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Dashboard V3 — the switch for the new (V3) look of the two dashboards:
//   • the MAIN UI    (everyone who is not on Limited Access)
//   • the LIMITED UI (users on Limited Access — see helpers/limited_access.py)
// Each one has its own setting, managed in Settings > Dashboard V3:   off  |  super_admin (only super admins see it)  |  global (everyone on that UI)
//
// For now V3 only shows a small tag in the bottom-left corner ("Main UI V3" / "Ltd UI V3"). All new V3 screens should branch on
// this:   const { v3On, activeUi } = useDashboardV3();   (activeUi is 'main' or 'limited'; v3On is true when V3 is switched on for THIS
// person on the UI they are looking at).
//
// TEST VIEW: a super admin is never on the Limited UI, so to test it they can switch between the Main UI and the Limited UI in
// Settings > Dashboard V3. That choice is kept in this browser only (localStorage) and is ignored for everyone who is not a super admin.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export const V3_MODES = ['off', 'super_admin', 'global'];
const TEST_VIEW_KEY = 'c30_v3_test_view';
const TEST_VIEW_EVENT = 'c30:v3-test-view';

export const getStoredTestView = () => {
  try { return localStorage.getItem(TEST_VIEW_KEY) === 'limited' ? 'limited' : 'main'; } catch (_) { return 'main'; }
};

export const setStoredTestView = (view) => {
  try {
    if (view === 'limited') localStorage.setItem(TEST_VIEW_KEY, 'limited');
    else localStorage.removeItem(TEST_VIEW_KEY);
  } catch (_) { /* private mode: the choice just won't persist */ }
  window.dispatchEvent(new Event(TEST_VIEW_EVENT));
};

/** Which UI a person is on, and whether V3 is on for them there. Pure, so it can be reasoned about (and tested) on its own. */
export const resolveDashboardV3 = (settings, user, testView) => {
  const isSuperAdmin = user?.role === 'super_admin';
  const effectiveTestView = isSuperAdmin ? (testView === 'limited' ? 'limited' : 'main') : 'main';
  const effectiveLimited = !!user?.is_limited_access || (isSuperAdmin && effectiveTestView === 'limited');
  const activeUi = effectiveLimited ? 'limited' : 'main';
  const raw = settings?.[`${activeUi}_ui`];
  const mode = V3_MODES.includes(raw) ? raw : 'off';
  const v3On = !!user && (mode === 'global' || (mode === 'super_admin' && isSuperAdmin));
  return { testView: effectiveTestView, effectiveLimited, activeUi, mode, v3On };
};

/** Which home page this person sees: true = the OG Dashboard (the saved copy of the home page as it was), false = the new home page. Settings > User Interface has two
 *  switches, both on to begin with: "OG Dashboard" (on = everyone keeps the OG Dashboard whatever is being built; off = everyone sees the new home page) and "Super admin
 *  view" (off = super admins alone see the new home page while everyone else still has the OG Dashboard). While settings are still loading, or fail to load, it is the OG Dashboard. */
export const resolveOgHome = (settings, user) => {
  const everyone = settings?.og_dashboard !== false;
  const superAdminView = settings?.og_dashboard_super_admin !== false;
  const isSuperAdmin = user?.role === 'super_admin';
  return everyone && (!isSuperAdmin || superAdminView);
};

/** The test view and the "is this person on the Limited UI" answer. Used by App.js to choose which UI to render, so it must work
 *  before the provider below exists. */
export const useDashboardV3Test = (user) => {
  const [stored, setStored] = useState(getStoredTestView);
  useEffect(() => {
    const sync = () => setStored(getStoredTestView());
    window.addEventListener(TEST_VIEW_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(TEST_VIEW_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const { testView, effectiveLimited } = resolveDashboardV3(null, user, stored);
  return { testView, effectiveLimited };
};

const DashboardV3Context = createContext({
  v3On: false,
  activeUi: 'main',
  mode: 'off',
  testView: 'main',
  effectiveLimited: false,
  ogHome: true,
  settings: { main_ui: 'off', limited_ui: 'off', og_dashboard: true, og_dashboard_super_admin: true },
  refresh: () => {},
});

export const DashboardV3Provider = ({ user, children }) => {
  const [settings, setSettings] = useState({ main_ui: 'off', limited_ui: 'off', og_dashboard: true, og_dashboard_super_admin: true });
  const { testView } = useDashboardV3Test(user);

  const refresh = useCallback(() => {
    if (!user) return;
    api.get(`${API}/dashboard-v3/settings`)
      .then((res) => setSettings({
        main_ui: res.data?.main_ui || 'off', limited_ui: res.data?.limited_ui || 'off',
        og_dashboard: res.data?.og_dashboard !== false, og_dashboard_super_admin: res.data?.og_dashboard_super_admin !== false,
      }))
      .catch(() => {}); // fail safe: V3 stays off
  }, [user]);

  useEffect(() => { refresh(); }, [refresh, user?.id]);

  const value = useMemo(
    () => ({ ...resolveDashboardV3(settings, user, testView), ogHome: resolveOgHome(settings, user), settings, refresh }),
    [settings, user, testView, refresh],
  );
  return <DashboardV3Context.Provider value={value}>{children}</DashboardV3Context.Provider>;
};

export const useDashboardV3 = () => useContext(DashboardV3Context);

/** The tag in the bottom-left corner while V3 is on for this person, plus — for a super admin who is testing the Limited UI — a
 *  one-click way back to the Main UI (so they can never get stuck in the test view). */
export const DashboardV3Tag = () => {
  const { v3On, activeUi, testView } = useDashboardV3();
  const impersonating = typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('_impersonated_as');
  if (!(v3On && activeUi === 'limited') && testView !== 'limited') return null;
  return (
    <div
      className="fixed left-2 flex items-center gap-1.5"
      style={{ bottom: impersonating ? 52 : 8, zIndex: 9000, pointerEvents: 'none' }}
      data-testid="dashboard-v3-tag-wrap"
    >
      {v3On && activeUi === 'limited' && (
        <span
          className="px-2.5 py-1 rounded-full text-[11px] font-extrabold tracking-wide text-white shadow-lg"
          style={{ background: '#7c3aed', pointerEvents: 'auto' }}
          data-testid="dashboard-v3-tag"
        >
          {activeUi === 'limited' ? 'Ltd UI V3' : 'Main UI V3'}
        </span>
      )}
      {testView === 'limited' && (
        <button
          type="button"
          onClick={() => setStoredTestView('main')}
          className="px-2.5 py-1 rounded-full text-[11px] font-bold shadow-lg"
          style={{ background: '#fff', color: '#7c3aed', border: '1.5px solid #7c3aed', pointerEvents: 'auto' }}
          data-testid="dashboard-v3-back-to-main"
          title="You are testing the Limited UI as a super admin — click to go back to the Main UI"
        >
          Testing Limited UI — back to Main UI
        </button>
      )}
    </div>
  );
};

export default DashboardV3Context;
